import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts';
import {
  fetchDadosEmpresa,
  atualizarDadosEmpresa,
  fetchUsuarios,
  atualizarUsuario,
  excluirUsuario,
  criarUsuario,
  fetchParametroFederal,
  salvarParametroFederal,
  fetchRegrasICMS,
  salvarRegraICMS,
  excluirRegraICMS,
  importarRegrasICMS,
  fetchRegrasIBSCBS,
  salvarRegraIBSCBS,
  excluirRegraIBSCBS,
  importarRegrasIBSCBS,
  fetchRegrasIbsCbsEntrada,
  importarConteudoFiscalIbsCbs,
  fetchCertificados,
  atualizarCertificadoAtivo,
  adicionarCertificado,
  fetchAuditoria,
} from '../api/ConfiguracoesApi';
import type {
  ParametroFederalApi,
  RegraDeParaICMSApi,
  RegraDeParaIBSCBSApi,
  ImportacaoConteudoFiscalApi,
  CertificadoAtivoApi,
  AtualizarUsuarioApi,
  CriarUsuarioApi,
} from '../types';

function useCnpjGestor() {
  const { user } = useAuth();
  return user?.cnpj ?? '';
}

export function useDadosEmpresa() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'dados-empresa', cnpjGestor],
    queryFn: fetchDadosEmpresa,
    staleTime: 5 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar dados da empresa' });
  }, [query.error, toast]);

  return query;
}

export function useAtualizarDadosEmpresa() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: () => atualizarDadosEmpresa(),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Dados cadastrais atualizados.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'dados-empresa', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao atualizar dados cadastrais.' });
    },
  });
}

export function useUsuarios() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'usuarios', cnpjGestor],
    queryFn: fetchUsuarios,
    staleTime: 2 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (query.error) {
      toast({ variant: 'destructive', title: 'Erro ao carregar usuários' });
      return;
    }
    if (query.data && !query.data.status.resultado) {
      toast({
        variant: 'destructive',
        title: query.data.status.mensagem || 'Erro ao carregar usuários',
      });
    }
  }, [query.error, query.data, toast]);

  return query;
}

export function useCriarUsuario() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (model: Omit<CriarUsuarioApi, 'CNPJ_Gestor'>) =>
      criarUsuario({ ...model, CNPJ_Gestor: cnpjGestor }),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Convite enviado por e-mail.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'usuarios', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao enviar convite.' });
    },
  });
}

export function useAtualizarUsuario() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (model: AtualizarUsuarioApi) => atualizarUsuario(model),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Usuário atualizado.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'usuarios', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao atualizar usuário.' });
    },
  });
}

export function useExcluirUsuario() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (email: string) => excluirUsuario(email),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Usuário removido.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'usuarios', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao remover usuário.' });
    },
  });
}

export function useParametroFederal() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'parametro-federal', cnpjGestor],
    queryFn: fetchParametroFederal,
    staleTime: 5 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar parâmetros federais' });
  }, [query.error, toast]);

  return query;
}

export function useSalvarParametroFederal() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (model: Partial<ParametroFederalApi>) => salvarParametroFederal(model),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Parâmetros salvos com sucesso.' });
        queryClient.invalidateQueries({
          queryKey: ['configuracoes', 'parametro-federal', cnpjGestor],
        });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao salvar parâmetros.' });
    },
  });
}

export function useRegrasICMS() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'regras-icms', cnpjGestor],
    queryFn: fetchRegrasICMS,
    staleTime: 2 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar regras ICMS' });
  }, [query.error, toast]);

  return query;
}

export function useSalvarRegraICMS() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (model: Partial<RegraDeParaICMSApi>) => salvarRegraICMS(model),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Regra ICMS salva com sucesso.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-icms', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao salvar regra ICMS.' });
    },
  });
}

export function useExcluirRegraICMS() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (id: number) => excluirRegraICMS(id),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Regra excluída.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-icms', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao excluir regra.' });
    },
  });
}

export function useImportarRegrasICMS() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (regras: Partial<RegraDeParaICMSApi>[]) => importarRegrasICMS(regras),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: response.status.mensagem || 'Regras importadas com sucesso.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-icms', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao importar regras.' });
    },
  });
}

export function useRegrasIBSCBS() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'regras-ibscbs', cnpjGestor],
    queryFn: fetchRegrasIBSCBS,
    staleTime: 2 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar regras IBS/CBS' });
  }, [query.error, toast]);

  return query;
}

export function useSalvarRegraIBSCBS() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (model: Partial<RegraDeParaIBSCBSApi>) => salvarRegraIBSCBS(model),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Regra IBS/CBS salva com sucesso.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-ibscbs', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao salvar regra IBS/CBS.' });
    },
  });
}

export function useExcluirRegraIBSCBS() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (id: number) => excluirRegraIBSCBS(id),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: 'Regra excluída.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-ibscbs', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao excluir regra.' });
    },
  });
}

export function useImportarRegrasIBSCBS() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (regras: Partial<RegraDeParaIBSCBSApi>[]) => importarRegrasIBSCBS(regras),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: response.status.mensagem || 'Regras importadas com sucesso.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-ibscbs', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao importar regras IBS/CBS.' });
    },
  });
}

export function useRegrasIbsCbsEntrada() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'regras-ibscbs-entrada', cnpjGestor],
    queryFn: fetchRegrasIbsCbsEntrada,
    staleTime: 2 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar as regras de IBS/CBS na entrada' });
  }, [query.error, toast]);

  return query;
}

export function useImportarConteudoFiscalIbsCbs() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (arquivo: ImportacaoConteudoFiscalApi) => importarConteudoFiscalIbsCbs(arquivo),
    onSuccess: response => {
      if (response.status.resultado) {
        toast({ title: response.status.mensagem || 'Arquivo importado.' });
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'regras-ibscbs', cnpjGestor] });
        queryClient.invalidateQueries({
          queryKey: ['configuracoes', 'regras-ibscbs-entrada', cnpjGestor],
        });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao importar o arquivo de regras de IBS/CBS.' });
    },
  });
}

export function useCertificados() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'certificados', cnpjGestor],
    queryFn: fetchCertificados,
    staleTime: 5 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar certificados' });
  }, [query.error, toast]);

  return query;
}

export function useAtualizarCertificadoAtivo() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (model: CertificadoAtivoApi) => atualizarCertificadoAtivo(model),
    onSuccess: response => {
      if (response.status.resultado) {
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'certificados', cnpjGestor] });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao atualizar certificado.' });
    },
  });
}

export function useAdicionarCertificado() {
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: ({ file, senha }: { file: File; senha: string }) =>
      adicionarCertificado(file, senha),
    onSuccess: response => {
      if (response.status.resultado) {
        queryClient.invalidateQueries({ queryKey: ['configuracoes', 'certificados', cnpjGestor] });
      }
    },
  });
}

export function useAuditoria() {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['configuracoes', 'auditoria', cnpjGestor],
    queryFn: fetchAuditoria,
    staleTime: 2 * 60 * 1000,
    enabled: !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({ variant: 'destructive', title: 'Erro ao carregar logs de auditoria' });
  }, [query.error, toast]);

  return query;
}
